// Panel principal del usuario autenticado
import { useMemo, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Trophy, BarChart3, Users, Zap, ArrowRight,
  TrendingUp, Target, Clock, Star, Lock
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import client from '../api/client'
import { parseBoliviaDate } from '../api/dateUtils'
import { useCommunities } from '../hooks/useCommunities'
import useAuthStore from '../store/useAuthStore'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import Button from '../components/ui/Button'
import Skeleton from '../components/ui/Skeleton'
import Flag from '../components/ui/Flag'

const FASE_NOMBRES = {
  grupos: 'Fase de Grupos',
  dieciseisavos: 'Dieciseisavos de Final',
  octavos: 'Octavos de Final',
  cuartos: 'Cuartos de Final',
  semifinal: 'Semifinales',
  tercer_puesto: 'Tercer Puesto',
  final: 'Final'
}

// Animación escalonada para las tarjetas
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
}

// Tarjeta de estadística rápida
function StatCard({ icon: Icon, label, value, subValue, subValueColor = 'text-text-muted', trend, color = 'text-cyan' }) {
  return (
    <motion.div variants={fadeUp}>
      <Card variant="default" padding="lg">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-text-muted font-body uppercase tracking-wider mb-1">{label}</p>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className={`text-2xl font-mono font-bold ${color}`}>{value}</span>
              {subValue && (
                <span className={`text-[11px] font-semibold font-body ${subValueColor}`}>{subValue}</span>
              )}
            </div>
            {trend && (
              <p className="text-xs text-success flex items-center gap-1 mt-1">
                <TrendingUp className="w-3 h-3" /> {trend}
              </p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-xl bg-surface-card flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </Card>
    </motion.div>
  )
}

function NextMatchCountdown({ match }) {
  const [timeLeft, setTimeLeft] = useState({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00'
  })

  useEffect(() => {
    if (!match) return

    const targetTime = parseBoliviaDate(match.fecha_partido).getTime()

    const updateTimer = () => {
      const now = new Date().getTime()
      const diff = targetTime - now

      if (diff <= 0) {
        setTimeLeft({
          days: '00',
          hours: '00',
          minutes: '00',
          seconds: '00'
        })
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeLeft({
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
      })
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [match])

  if (!match) return null

  const matchDate = parseBoliviaDate(match.fecha_partido)
  const formattedTime = matchDate.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
  const groupText = match.grupo 
    ? `Grupo ${match.grupo} · J${match.jornada || '1'} ${formattedTime}`
    : `${FASE_NOMBRES[match.fase] || match.fase} · ${formattedTime}`

  return (
    <Card variant="default" padding="lg">
      <Card.Header className="pb-3 flex flex-col items-start gap-1">
        <h3 className="font-display font-semibold text-text-primary text-lg sm:text-xl flex items-center gap-2">
          Próximos partidos
        </h3>
        <p className="text-xs text-text-muted">
          Predice antes del pitazo inicial. Cada pick suma.
        </p>
      </Card.Header>

      <div className="space-y-4 pt-2">
        <div className="bg-navy-dark/40 border border-slate-700/30 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-warning/80 uppercase tracking-widest mb-4">
            <Clock className="w-3.5 h-3.5 text-warning" />
            <span>Próximo Partido</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="flex-1 flex flex-col gap-2">
              {/* Escritorio */}
              <div className="hidden md:flex items-center gap-3">
                <div className="w-12 h-8 bg-surface-card rounded-lg flex items-center justify-center border border-slate-700/50 shadow-inner overflow-hidden shrink-0">
                  <Flag code={match.equipo_local_bandera} className="w-8 h-5.5 object-cover rounded" />
                </div>
                <span className="text-lg font-bold text-text-primary">{match.equipo_local_nombre}</span>
                <span className="text-sm font-semibold text-text-muted px-1">VS</span>
                <span className="text-lg font-bold text-text-primary">{match.equipo_visitante_nombre}</span>
                <div className="w-12 h-8 bg-surface-card rounded-lg flex items-center justify-center border border-slate-700/50 shadow-inner overflow-hidden shrink-0">
                  <Flag code={match.equipo_visitante_bandera} className="w-8 h-5.5 object-cover rounded" />
                </div>
              </div>

              <div className="hidden md:block text-xs text-text-muted">
                {groupText}
              </div>

              {/* Móvil */}
              <div className="md:hidden space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-7 bg-surface-card rounded-md flex items-center justify-center border border-slate-700/50 shadow-inner overflow-hidden shrink-0">
                      <Flag code={match.equipo_local_bandera} className="w-6.5 h-4.5 object-cover rounded" />
                    </div>
                    <span className="text-sm font-bold text-text-primary">{match.equipo_local_nombre}</span>
                  </div>
                  <span className="text-xs font-semibold text-text-muted pr-2">VS</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-text-primary">{match.equipo_visitante_nombre}</span>
                    <div className="w-10 h-7 bg-surface-card rounded-md flex items-center justify-center border border-slate-700/50 shadow-inner overflow-hidden shrink-0">
                      <Flag code={match.equipo_visitante_bandera} className="w-6.5 h-4.5 object-cover rounded" />
                    </div>
                  </div>
                  <span className="text-xs text-text-muted font-medium">{groupText}</span>
                </div>
              </div>
            </div>

            {/* Temporizador */}
            <div className="flex items-center justify-center md:justify-end gap-2 shrink-0">
              <div className="w-14 h-16 sm:w-16 sm:h-20 bg-surface-card/60 border border-slate-700/30 rounded-xl flex flex-col items-center justify-center shadow-md">
                <span className="text-xl sm:text-2xl font-mono font-bold text-amber-400 leading-none">{timeLeft.days}</span>
                <span className="text-[8px] sm:text-[9px] font-semibold text-text-muted mt-1.5 tracking-wider">DÍAS</span>
              </div>
              <div className="w-14 h-16 sm:w-16 sm:h-20 bg-surface-card/60 border border-slate-700/30 rounded-xl flex flex-col items-center justify-center shadow-md">
                <span className="text-xl sm:text-2xl font-mono font-bold text-amber-400 leading-none">{timeLeft.hours}</span>
                <span className="text-[8px] sm:text-[9px] font-semibold text-text-muted mt-1.5 tracking-wider">HRS</span>
              </div>
              <div className="w-14 h-16 sm:w-16 sm:h-20 bg-surface-card/60 border border-slate-700/30 rounded-xl flex flex-col items-center justify-center shadow-md">
                <span className="text-xl sm:text-2xl font-mono font-bold text-amber-400 leading-none">{timeLeft.minutes}</span>
                <span className="text-[8px] sm:text-[9px] font-semibold text-text-muted mt-1.5 tracking-wider">MIN</span>
              </div>
              <div className="w-14 h-16 sm:w-16 sm:h-20 bg-surface-card/60 border border-slate-700/30 rounded-xl flex flex-col items-center justify-center shadow-md">
                <span className="text-xl sm:text-2xl font-mono font-bold text-amber-400 leading-none">{timeLeft.seconds}</span>
                <span className="text-[8px] sm:text-[9px] font-semibold text-text-muted mt-1.5 tracking-wider">SEG</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export default function Dashboard() {
  const { user } = useAuthStore()
  const { communities, isLoadingCommunities, leaderboard, isLoadingLeaderboard } = useCommunities()

  // 1. Obtener la predicción del usuario
  const { data: predictionsData, isLoading: isLoadingPreds } = useQuery({
    queryKey: ['userPredictions'],
    queryFn: async () => {
      const response = await client.get('/predictions')
      return response.data
    }
  })
  const userPredictions = predictionsData?.datos || []
  const hasPredictions = userPredictions.length > 0

  // 2. Obtener todos los partidos para calcular los próximos partidos reales
  const { data: matchesData, isLoading: isLoadingMatches } = useQuery({
    queryKey: ['user-all-matches'],
    queryFn: async () => {
      const response = await client.get('/matches')
      return response.data?.datos || []
    },
    // Refresco automático de 30 segundos si hay partidos en juego
    refetchInterval: (query) => {
      const matches = query.state.data || []
      const hasLive = matches.some(m => m.estado === 'en_juego')
      return hasLive ? 30000 : false
    }
  })
  const matches = matchesData || []

  // Calcular los partidos que están actualmente en juego/vivo
  const liveMatches = useMemo(() => {
    return matches
      .map(m => ({
        ...m,
        grupo: m.grupo_nombre
      }))
      .filter(m => m.estado === 'en_juego')
  }, [matches])

  // Calcular los próximos 3 partidos programados a partir del calendario real en la DB
  const upcomingMatches = useMemo(() => {
    return matches
      .map(m => ({
        ...m,
        grupo: m.grupo_nombre
      }))
      .filter(m => m.estado === 'programado')
      .sort((a, b) => parseBoliviaDate(a.fecha_partido) - parseBoliviaDate(b.fecha_partido))
      .slice(0, 3)
  }, [matches])

  // Filtrar los partidos que se mostrarán en la lista inferior de próximos partidos
  const displayUpcomingMatches = useMemo(() => {
    return liveMatches.length > 0 ? upcomingMatches : upcomingMatches.slice(1)
  }, [liveMatches, upcomingMatches])

  // Obtener puntos y posición del usuario
  const userRankItem = leaderboard.find(item => Number(item.usuario_id) === Number(user?.id))
  const points = userRankItem ? Number(userRankItem.puntos) : 0
  const rank = userRankItem ? Number(userRankItem.posicion) : '-'

  const stats = {
    points,
    rank,
    predictions: hasPredictions ? '100%' : '0%',
    predictionsSub: hasPredictions ? '(Completas)' : '(Pendiente)',
    predictionsColor: hasPredictions ? 'text-success/90' : 'text-cyan/80',
    accuracy: userRankItem ? '72%' : '—',
  }

  // Actividad reciente dinámica
  const recentActivity = useMemo(() => {
    const act = []
    if (hasPredictions) {
      act.push({ text: 'Predicciones enviadas y guardadas', time: 'Mundial 2026', icon: Lock })
    } else {
      act.push({ text: 'Aún no has enviado tus predicciones', time: 'Pendiente', icon: Target })
    }
    communities.slice(0, 2).forEach(l => {
      act.push({ text: `Te uniste a la comunidad "${l.nombre}"`, time: 'Miembro', icon: Users })
    })
    return act
  }, [hasPredictions, communities])

  const isLoading = isLoadingCommunities || isLoadingLeaderboard || isLoadingPreds || isLoadingMatches

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-12 w-64 rounded-xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-40 w-full rounded-2xl" />
            <Skeleton className="h-60 w-full rounded-2xl" />
          </div>
          <Skeleton className="h-96 w-full rounded-2xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Saludo */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary">
          ¡Hola, {user?.nombre || 'Jugador'}! ⚽
        </h1>
        <p className="text-text-secondary mt-1">
          Tu resumen de predicciones para el Mundial 2026
        </p>
      </motion.div>

      {/* Estadísticas rápidas */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
      >
        <StatCard icon={Zap} label="Puntos" value={stats.points} trend={stats.points > 0 ? "+ puntos reales" : null} />
        <StatCard icon={BarChart3} label="Posición" value={`#${stats.rank}`} color="text-warning" />
        <StatCard 
          icon={Target} 
          label="Predicciones" 
          value={stats.predictions} 
          subValue={stats.predictionsSub}
          subValueColor={stats.predictionsColor}
        />
        <StatCard icon={Star} label="Precisión" value={stats.accuracy} color="text-success" />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Partidos en Vivo o Próximo Partido con Cuenta Regresiva */}
          {liveMatches.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card variant="default" padding="lg" className="border border-warning/30 bg-warning/5 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-warning/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <Card.Header className="pb-3 border-b border-warning/10 flex justify-between items-center">
                  <h3 className="font-display font-bold text-warning flex items-center gap-2 text-sm sm:text-base">
                    <span className="w-2.5 h-2.5 rounded-full bg-warning animate-pulse shrink-0" />
                    ¡Partidos En Vivo / En Juego! 🔥
                  </h3>
                  <Badge variant="warning" size="sm" className="animate-pulse">En Directo</Badge>
                </Card.Header>
                <div className="space-y-3 pt-3">
                  {liveMatches.map((match) => (
                    <div
                      key={match.id}
                      className="flex items-center justify-between p-3 rounded-xl bg-navy-dark/60 border border-warning/10 hover:border-warning/30 transition-all"
                    >
                      {/* Local */}
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <Flag code={match.equipo_local_bandera} className="w-5 h-3.5 object-cover rounded shadow shrink-0" />
                        <span className="text-sm font-semibold truncate text-text-primary">{match.equipo_local_nombre}</span>
                      </div>
                      
                      {/* Marcador */}
                      <div className="mx-3 px-2.5 py-1 bg-warning/10 border border-warning/20 rounded-lg font-mono font-bold text-sm text-warning flex items-center gap-1.5 shrink-0">
                        <span>{match.goles_local ?? 0}</span>
                        <span className="text-text-muted font-normal text-xs">-</span>
                        <span>{match.goles_visitante ?? 0}</span>
                      </div>

                      {/* Visitante */}
                      <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
                        <span className="text-sm font-semibold truncate text-text-primary text-right">{match.equipo_visitante_nombre}</span>
                        <Flag code={match.equipo_visitante_bandera} className="w-5 h-3.5 object-cover rounded shadow shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          ) : (
            upcomingMatches.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <NextMatchCountdown match={upcomingMatches[0]} />
              </motion.div>
            )
          )}

          {/* Acción principal: completar o ver predicciones */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {hasPredictions ? (
              <Card variant="default" padding="lg" className="relative overflow-hidden border border-success/30 bg-success/5">
                <div className="absolute top-0 right-0 w-32 h-32 bg-success/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative">
                  <Badge variant="success" size="sm" className="mb-3" icon={Lock}>Predicción Completada</Badge>
                  <h3 className="text-lg font-display font-bold text-text-primary mb-2">
                    ¡Tu ruta del Mundial está lista!
                  </h3>
                  <p className="text-sm text-text-secondary mb-4 max-w-lg">
                    Has guardado y enviado tus pronósticos exitosamente. Tu campeón elegido es {userPredictions[0]?.campeon_nombre || ''}. Puedes revisar todos tus picks paso a paso en el asistente.
                  </p>
                  <Link to="/predicciones">
                    <Button size="md" iconRight={ArrowRight}>
                      Ver mis predicciones
                    </Button>
                  </Link>
                </div>
              </Card>
            ) : (
              <Card variant="cyan" padding="lg" className="relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative">
                  <Badge variant="cyan" size="sm" className="mb-3">Acción recomendada</Badge>
                  <h3 className="text-lg font-display font-bold text-text-primary mb-2">
                    Completa tu predicción
                  </h3>
                  <p className="text-sm text-text-secondary mb-4 max-w-lg">
                    Aún te faltan predicciones por hacer. Completa tu bracket para acumular puntos cuando inicien los partidos.
                  </p>
                  <Link to="/predicciones">
                    <Button size="md" iconRight={ArrowRight}>
                      Ir a predicciones
                    </Button>
                  </Link>
                </div>
              </Card>
            )}
          </motion.div>

          {/* Próximos partidos (restantes o completos) */}
          {displayUpcomingMatches.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card variant="default" padding="lg">
                <Card.Header>
                  <h3 className="font-display font-semibold text-text-primary flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan" />
                    Próximos partidos
                  </h3>
                  <Badge variant="default" size="sm">{upcomingMatches[0]?.grupo ? 'Fase de grupos' : 'Eliminatorias'}</Badge>
                </Card.Header>
                <div className="space-y-3">
                  {displayUpcomingMatches.map((match) => (
                    <div
                      key={match.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-surface-card hover:bg-surface-card-hover transition-colors"
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary truncate flex items-center gap-1.5">
                          <Flag code={match.equipo_local_bandera} className="w-4 h-3 object-cover rounded shadow-sm shrink-0" />
                          <span>{match.equipo_local_nombre}</span>
                        </span>
                        <span className="text-text-muted font-mono text-xs">vs</span>
                        <span className="text-sm font-medium text-text-primary truncate flex items-center gap-1.5">
                          <Flag code={match.equipo_visitante_bandera} className="w-4 h-3 object-cover rounded shadow-sm shrink-0" />
                          <span>{match.equipo_visitante_nombre}</span>
                        </span>
                      </div>
                      <div className="text-right shrink-0 flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs font-mono text-cyan">
                            {parseBoliviaDate(match.fecha_partido).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                          </p>
                          <p className="text-xs text-text-muted">
                            {parseBoliviaDate(match.fecha_partido).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <Badge variant="default" size="sm">{match.grupo ? `Grupo ${match.grupo}` : (FASE_NOMBRES[match.fase] || match.fase)}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Columna lateral */}
        <div className="space-y-6">
          {/* Actividad reciente */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card variant="default" padding="lg">
              <Card.Header>
                <h3 className="font-display font-semibold text-text-primary text-sm">
                  Actividad reciente
                </h3>
              </Card.Header>
              <div className="space-y-4">
                {recentActivity.map((activity, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-8 h-8 rounded-lg bg-surface-card flex items-center justify-center shrink-0">
                      <activity.icon className="w-4 h-4 text-cyan" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary">{activity.text}</p>
                      <p className="text-xs text-text-muted">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Acceso rápido a comunidades */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card variant="default" padding="lg">
              <Card.Header>
                <h3 className="font-display font-semibold text-text-primary text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-cyan" />
                  Mis comunidades
                </h3>
              </Card.Header>
              <div className="space-y-2 mb-4">
                {communities.slice(0, 3).map(league => (
                  <div key={league.id} className="flex items-center justify-between p-2 rounded-lg bg-surface-card">
                    <span className="text-sm text-text-primary">{league.nombre}</span>
                    <Badge variant="cyan" size="sm">{league.total_miembros} miemb.</Badge>
                  </div>
                ))}
                {communities.length === 0 && (
                  <p className="text-xs text-text-muted py-2">No estás en ninguna comunidad.</p>
                )}
              </div>
              <Link to="/comunidades">
                <Button variant="ghost" size="sm" className="w-full text-center block" iconRight={ArrowRight}>
                  Ver todas las comunidades
                </Button>
              </Link>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
